import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AlertCircle, ArrowRight, CheckCircle2, ChevronRight, FileText, Lock, Mail, RefreshCw, Smartphone, Store } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import ContactField from '@/components/auth/ContactField';
import OtpAuthLayout from '@/components/auth/OtpAuthLayout';
import { Alert, Button, Card, Divider, HelpLinkCard, Icon, InlineStatus, OtpInput, ResultState, StepTransition } from '@/components/common';
import { loginSuccess } from '@/store/slices/authSlice';
import { LoginContact, OtpCustomer, requestLoginOtp, verifyLoginOtp } from '@/service/otpAuthService';
import { ContactChannel, contactError, displayContact, FIND_PLAN_PATH, toContact } from '@/utils/loginContact';

type Step =
  | { kind: 'contact'; notLinked?: boolean; error?: string }
  | { kind: 'code'; status: 'idle' | 'invalid' | 'expired'; title?: string; message?: string }
  | { kind: 'locked'; message: string }
  | { kind: 'welcome'; token: string; customer: OtpCustomer };

interface LocationState {
  from?: { pathname: string };
}

const OTP_LENGTH = 6;
const LABELS = { mobile: 'Mobile number used at checkout', email: 'Email used at checkout' };

const formatCountdown = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/** Copy that differs between signing in by mobile (code on WhatsApp) and by email. */
const COPY = {
  mobile: { word: 'number', other: 'email', send: 'Send code on WhatsApp', resend: 'Resend code on WhatsApp', sentVia: 'sent on WhatsApp', hint: 'Didn’t get it?' },
  email: { word: 'email', other: 'number', send: 'Send code by email', resend: 'Resend code by email', sentVia: 'sent to your email', hint: 'Didn’t get it? Check spam, or' },
};

const Terms: React.FC = () => (
  <p className="tw-text-center tw-text-[12px] tw-leading-4 tw-text-ds-text-tertiary">
    By continuing you agree to Protega’s{' '}
    <Link to="/terms" className="hover:tw-text-ds-text-primary hover:tw-underline">
      Terms of Use
    </Link>{' '}
    and{' '}
    <Link to="/privacy-policy" className="hover:tw-text-ds-text-primary hover:tw-underline">
      Privacy Policy
    </Link>
    .
  </p>
);

const CantSignIn: React.FC = () => (
  <HelpLinkCard to={FIND_PLAN_PATH} icon={FileText} title="Can’t sign in?" body="Find your plan with your order ID and invoice" />
);

/** L1–L8: sign in with a one-time code sent to the customer's mobile (WhatsApp) or email. */
const CustomerOtpLogin: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation() as { state?: LocationState };
  const redirectTo = location.state?.from?.pathname || '/customer/policies';

  const [step, setStep] = useState<Step>({ kind: 'contact' });
  const [channel, setChannel] = useState<ContactChannel>('mobile');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  /** The contact the current code was sent to. */
  const [sentTo, setSentTo] = useState<LoginContact | null>(null);
  const [otp, setOtp] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [expiresInMinutes, setExpiresInMinutes] = useState(5);
  /** When the current code stops working (ms since epoch). */
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const contactInput = useRef<HTMLInputElement>(null);

  const contact = toContact(channel, mobile, email);
  const fieldError = contactError(channel, mobile, email, touched);
  const display = sentTo ? displayContact(sentTo) : contact ? displayContact(contact) : '';
  const copy = COPY[channel];
  const fieldLocked = step.kind === 'code' || (step.kind === 'contact' && Boolean(step.notLinked));

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendIn]);

  // Focus the field when it's editable again, and when switching between mobile and email.
  useEffect(() => {
    if (step.kind === 'contact' && !step.notLinked) contactInput.current?.focus();
  }, [step, channel]);

  // Once the code's validity runs out, show the expired state straight away (L6).
  const showExpired = useCallback(() => {
    setOtp('');
    setStep({
      kind: 'code',
      status: 'expired',
      title: 'This code has expired',
      message: `Codes stay valid for ${expiresInMinutes} minutes. Request a new one to continue.`,
    });
  }, [expiresInMinutes]);

  const codeLive = step.kind === 'code' && step.status !== 'expired';
  useEffect(() => {
    if (!codeLive || !expiresAt) return;
    const id = window.setTimeout(showExpired, Math.max(0, expiresAt - Date.now()));
    return () => window.clearTimeout(id);
  }, [codeLive, expiresAt, showExpired]);

  const run = async (task: () => Promise<void>) => {
    setLoading(true);
    try {
      await task();
    } catch (err) {
      console.error('OTP sign-in error:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = () =>
    run(async () => {
      const onCodeStep = step.kind === 'code';
      const to = onCodeStep && sentTo ? sentTo : contact;
      if (!to) return;
      const result = await requestLoginOtp(to);
      setSentTo(to);
      switch (result.status) {
        case 'sent':
          setOtp('');
          setResendIn(result.resendInSeconds);
          setExpiresInMinutes(Math.max(1, Math.round(result.expiresInSeconds / 60)));
          setExpiresAt(Date.now() + result.expiresInSeconds * 1000);
          setStep({ kind: 'code', status: 'idle' });
          break;
        case 'cooldown':
          // A code is already on its way; just restart the timer.
          setResendIn(result.seconds);
          if (onCodeStep) toast.error(result.message);
          else setStep({ kind: 'code', status: 'idle' });
          break;
        case 'not_linked':
          setStep({ kind: 'contact', notLinked: true });
          break;
        case 'locked':
          setStep({ kind: 'locked', message: result.message });
          break;
        case 'failed':
          if (onCodeStep) toast.error(result.message);
          else setStep({ kind: 'contact', error: result.message });
          break;
      }
    });

  const verifyOtp = () =>
    run(async () => {
      if (!sentTo) return;
      const result = await verifyLoginOtp(sentTo, otp);
      switch (result.status) {
        case 'verified':
          setStep({ kind: 'welcome', token: result.token, customer: result.customer });
          break;
        case 'invalid':
          setStep({ kind: 'code', status: 'invalid', message: result.message });
          break;
        case 'expired':
          if (result.reason === 'expired') showExpired();
          else {
            setOtp('');
            setStep({ kind: 'code', status: 'expired', title: result.title, message: result.message });
          }
          break;
        case 'locked':
          setStep({ kind: 'locked', message: result.message });
          break;
        case 'failed':
          toast.error(result.message);
          break;
      }
    });

  // The session starts here rather than on verify: once signed in, PublicRoute would
  // redirect away before the welcome screen could show.
  const goToDashboard = () => {
    if (step.kind !== 'welcome') return;
    const { token, customer } = step;
    if (token) {
      const { user } = customer;
      dispatch(
        loginSuccess({
          user: { name: user.name, email: user.email, phone: user.phone ?? null, address: (user.address as string) ?? null },
          accessToken: token,
          loginType: 'regular',
        }),
      );
    }
    navigate(redirectTo, { replace: true });
  };

  const changeContact = (clear = false) => {
    if (clear) {
      setMobile('');
      setEmail('');
    }
    setTouched(false);
    setOtp('');
    setSentTo(null);
    setStep({ kind: 'contact' });
  };

  const switchChannel = (next: ContactChannel) => {
    setChannel(next);
    changeContact();
  };

  const handleOtpChange = (value: string) => {
    setOtp(value);
    if (step.kind === 'code' && step.status === 'invalid') setStep({ kind: 'code', status: 'idle' });
  };

  /** "Didn't get it? Resend in 0:24", then a "Resend code" link once the timer ends. */
  const resendLine = (lead?: string) => {
    // Lower-case after "…Check spam, or".
    const verb = lead?.endsWith(' or') ? 'resend' : 'Resend';
    return (
      <p className="tw-text-[12px] tw-leading-4 tw-text-ds-text-tertiary">
        {lead && `${lead} `}
        {resendIn > 0 ? (
          `${verb} in ${formatCountdown(resendIn)}`
        ) : (
          <button type="button" onClick={sendOtp} disabled={loading} className="tw-font-semibold tw-text-ds-brand-text hover:tw-underline disabled:tw-opacity-60">
            {verb} code
          </button>
        )}
      </p>
    );
  };

  /** What sits under the field: the send button, "no purchases", or the code entry. */
  const renderBelowField = () => {
    if (step.kind === 'contact' && step.notLinked) {
      return (
        <>
          <Alert tone="danger" title={`No purchases found for ${display}`}>
            Check the {copy.word}, or try the {copy.other} you gave at checkout. New orders can take a few minutes to appear.
          </Alert>
          <Button fullWidth onClick={() => changeContact(true)}>
            Try another {copy.word}
          </Button>
          <CantSignIn />
        </>
      );
    }
    if (step.kind === 'code') {
      const expired = step.status === 'expired';
      return (
        <>
          <div className="tw-flex tw-flex-col tw-gap-2">
            <p className="tw-text-[13px] tw-font-medium tw-leading-[18px] tw-text-ds-text-secondary">Enter the 6-digit code {copy.sentVia}</p>
            <OtpInput
              value={otp}
              onChange={handleOtpChange}
              length={OTP_LENGTH}
              error={step.status === 'invalid'}
              disabled={loading}
              autoFocus
              label={`6-digit code ${copy.sentVia}`}
            />
            {step.status === 'invalid' && (
              <>
                <InlineStatus icon={AlertCircle} tone="danger">
                  {step.message}
                </InlineStatus>
                {resendLine()}
              </>
            )}
            {step.status === 'idle' && resendLine(copy.hint)}
          </div>
          {expired ? (
            <>
              <Alert tone="warning" title={step.title}>
                {step.message}
              </Alert>
              <Button fullWidth leadingIcon={RefreshCw} onClick={sendOtp} loading={loading}>
                {copy.resend}
              </Button>
            </>
          ) : (
            <>
              <Button type="submit" fullWidth disabled={otp.length < OTP_LENGTH} loading={loading}>
                Verify and continue
              </Button>
              {step.status === 'idle' && <Terms />}
            </>
          )}
        </>
      );
    }
    return (
      <>
        {step.kind === 'contact' && step.error && (
          <Alert tone="danger" title="We couldn’t send your code">
            {step.error}
          </Alert>
        )}
        <Button type="submit" fullWidth disabled={!contact} loading={loading}>
          {step.kind === 'contact' && step.error ? 'Try again' : copy.send}
        </Button>
        <CantSignIn />
        <Terms />
      </>
    );
  };

  const belowKey = step.kind === 'code' ? 'code' : step.kind === 'contact' && step.notLinked ? 'not-linked' : 'send';

  const renderStep = () => {
    switch (step.kind) {
      case 'contact':
      case 'code':
        return (
          <form
            className="tw-flex tw-flex-col tw-gap-5"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (step.kind === 'code') {
                // An expired code can't be verified; the customer has to resend first.
                if (otp.length === OTP_LENGTH && step.status !== 'expired') verifyOtp();
              } else if (contact) sendOtp();
            }}
          >
            <div className="tw-flex tw-flex-col tw-gap-2">
              <h1 className="tw-text-[28px] tw-font-bold tw-leading-[34px] tw-tracking-[-0.28px] tw-text-ds-text-primary">Sign in to Protega</h1>
              <p className="tw-text-[15px] tw-leading-[22px] tw-text-ds-text-secondary">
                See your plans and raise claims for purchases from any brand protected by Protega.
              </p>
            </div>
            <ContactField
              inputRef={contactInput}
              channel={channel}
              onChannelChange={switchChannel}
              mobile={mobile}
              onMobileChange={(digits) => {
                setMobile(digits);
                if (step.kind === 'contact' && step.error) setStep({ kind: 'contact' });
              }}
              email={email}
              onEmailChange={(value) => {
                setEmail(value);
                if (step.kind === 'contact' && step.error) setStep({ kind: 'contact' });
              }}
              labels={LABELS}
              onBlur={() => setTouched(true)}
              error={fieldLocked ? undefined : fieldError}
              locked={fieldLocked}
              onChange={() => changeContact()}
            />
            <StepTransition stepKey={belowKey} className="tw-flex tw-flex-col tw-gap-5">
              {renderBelowField()}
            </StepTransition>
          </form>
        );

      case 'locked':
        return (
          <div className="tw-flex tw-flex-col tw-gap-5">
            <ResultState tone="danger" icon={Lock} title="Too many attempts" description={step.message} />
            <Button fullWidth leadingIcon={channel === 'mobile' ? Mail : Smartphone} onClick={() => switchChannel(channel === 'mobile' ? 'email' : 'mobile')}>
              Use {channel === 'mobile' ? 'email' : 'mobile'} instead
            </Button>
            <CantSignIn />
          </div>
        );

      case 'welcome': {
        const { customer } = step;
        const firstName = customer.user.name?.trim().split(/\s+/)[0];
        return (
          <div className="tw-flex tw-flex-col tw-gap-5">
            <ResultState
              tone="success"
              icon={CheckCircle2}
              title={firstName ? `Welcome, ${firstName}` : 'Welcome back'}
              description={
                customer.productCount !== undefined ? `We found ${customer.productCount} products linked to ${display}.` : `You’re signed in with ${display}.`
              }
            />
            {!!customer.brands?.length && (
              <Card className="tw-flex tw-flex-col tw-gap-3">
                {customer.brands.map((brand, i) => (
                  <React.Fragment key={brand.name}>
                    {i > 0 && <Divider />}
                    <button
                      type="button"
                      onClick={goToDashboard}
                      className="tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-text-left focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand"
                    >
                      <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-[10px] tw-bg-ds-subtle">
                        <Icon icon={Store} size={20} className="tw-text-ds-brand-text" />
                      </span>
                      <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
                        <span className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{brand.name}</span>
                        <span className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{brand.summary}</span>
                      </span>
                      <Icon icon={ChevronRight} className="tw-text-ds-text-tertiary" />
                    </button>
                  </React.Fragment>
                ))}
              </Card>
            )}
            <p className="tw-text-center tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">
              Bought from another brand protected by Protega? Those purchases appear here automatically.
            </p>
            <Button fullWidth trailingIcon={ArrowRight} onClick={goToDashboard}>
              Go to my dashboard
            </Button>
          </div>
        );
      }
    }
  };

  const viewKey = step.kind === 'locked' || step.kind === 'welcome' ? step.kind : 'form';

  return (
    <OtpAuthLayout>
      <StepTransition stepKey={viewKey}>{renderStep()}</StepTransition>
    </OtpAuthLayout>
  );
};

export default CustomerOtpLogin;
